import React, { useRef, useState } from "react";

import { Button, LinearProgress } from "@mui/material";
import { toDataSourceRequest } from "@progress/kendo-data-query";
import { getter } from "@progress/kendo-react-common";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import {
  Grid,
  GridColumn,
  GridToolbar,
  getSelectedState,
} from "@progress/kendo-react-grid";
import axiosStatic from "axios";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { useDataFilterApi } from "../../hooks/useFilterDataApi";
import { LoadingPanelComponent } from "../loading/LoadingPanel";
import { DropDownFilterCell } from "../sys-common/DropDownFilterCell";
import { excelDataNormalize } from "./excelDataNormalize";
import GridCellSelector from "./GridCellSelector";
import CustomGridToolbar from "./GridToolBar";
import TextFilterCell from "./TextFilterCell";
import { YesNoDropdownFilterCell } from "./YesNoFilterCell";

const dataGridContext = React.createContext({
  reFetch: () =>
    new Promise((res) => {
      res();
    }),
  data: { total: 0, data: [] },
  dataState: {},
  /**
   * @type {ExcelExport}
   */
  exporter: {},
  /**
   * @type {Grid}
   */
  grid: {},
  /**
   *
   * @param {React.SetStateAction<{ data: never[]}>} value
   * @returns
   */
  setSelectedList: (value) => null,
  /**
   * @type { [import("@progress/kendo-react-grid").GridColumnProps[], React.Dispatch<React.SetStateAction<GridColumnProps[]>>]}
   */
  columnState: () => null,
  /**
   * @type {import("@progress/kendo-react-grid").GridColumnProps[]}
   */
  initialColumns: [],
  filterInputValues: {},
});

export const useDataState = () => React.useContext(dataGridContext);

/**
 * @template T
 * @typedef KendoDataGridProps
 * @prop {Array<import("@progress/kendo-react-grid").GridColumnProps>} columns - Grid kolonlarının konfigürasyonlarını içeren dizi
 * // Basic columns configuration
 * const columns = [
 *   { field: 'id', title: 'ID', width: 100 },
 *   { field: 'name', title: 'Name', width: 200 },
 *   {
 *     field: 'status',
 *     title: 'Status',
 *     type: 'boolean',
 *     cell: (props) => <StatusCell {...props} />
 *   }
 * ];
 * 
 *
 * @prop {string} columnUniqueKey - Kullanıcının sayfayı yenilediğinde column yapısının en son hali ile yüklenmesi için gerekli key
 * // Column persistence key
 * const columnUniqueKey = "userGrid_columns_v1";
 *
 * @prop {import("axios").AxiosInstance} [axios] - Özel axios instance'ı (cache veya middleware yapılandırması için)
 * // Custom axios instance with cache
 * const axiosInstance = axios.create({
 *   baseURL: 'https://api.example.com',
 *   headers: { 'Cache-Control': 'max-age=300' }
 * });
 *
 * @prop {string} [uri] - API endpoint URL'i (axios.baseURL tanımlı değilse zorunlu)
 * // API endpoint
 * const uri = "/api/users";
 *
 * @prop {string} [dataItemKey="id"] - Unique tanımlayıcı alan adı
 * // Custom unique identifier
 * const dataItemKey = "userId";
 *
 * @prop {import("@progress/kendo-data-query").DataSourceRequestState} [dataState] - Grid'in başlangıç durumu
 * // Initial grid state
 * const initialState = {
 *   take: 10,
 *   skip: 0,
 *   sort: [{ field: 'name', dir: 'asc' }],
 *   filter: {
 *     logic: 'and',
 *     filters: [{ field: 'status', operator: 'eq', value: true }]
 *   }
 * };
 *
 * @prop {Array<{title: string, getUrl: (dataItem: T) => string}>} [gridButtons] - Grid satırlarında görüntülenecek aksiyon butonları
 * // Action buttons configuration
 * const gridButtons = [
 *   {
 *     title: 'Edit',
 *     getUrl: (item) => `/users/edit/${item.id}`
 *   },
 *   {
 *     title: 'View Details',
 *     getUrl: (item) => `/users/${item.id}`
 *   }
 * ];
 *
 * @prop {boolean} [notFetch=false] - Otomatik veri çekmeyi engeller
 * @prop {string} [title] - Grid başlığı
 * @prop {boolean} [pageable=true] - Sayfalama özelliği
 * @prop {(data: T[]) => T[]} [onExcel] - Excel export öncesi veri işleme
 * // Excel export data transformation
 * const handleExcel = (data) => {
 *   return data.map(item => ({
 *     ...item,
 *     formattedDate: formatDate(item.date),
 *     statusText: item.status ? 'Active' : 'Inactive'
 *   }));
 * };
 *
 * @prop {React.ReactNode} [topButtons] - Toolbar'da gösterilecek özel butonlar
 * // Custom toolbar buttons
 * const customButtons = (
 *   <>
 *     <Button onClick={handleAdd}>Add New</Button>
 *     <Button onClick={handleRefresh}>Refresh</Button>
 *   </>
 * );
 *
 * @prop {string} [hrefTarget="_blank"] - Link hedef özelliği
 * @prop {boolean} [selectable=false] - Satır seçimi özelliği
 * @prop {"Up"|"Down"} [childrenLocation="Up"] - Alt bileşenlerin konumu
 * @prop {boolean} [inLineEdit=false] - Satır içi düzenleme özelliği
 * @prop {(selectedState: object, data: T[]) => void} [onSelectChange] - Seçim değişikliği callback'i
 * // Selection change handler
 * const handleSelectionChange = (selectedState, data) => {
 *   const selectedItems = data.filter(item => selectedState[item.id]);
 *   console.log('Selected items:', selectedItems);
 * };
 *
 * @prop {(tr: React.ReactElement, dataItem: T) => React.ReactElement} [rowRender] - Özel satır render fonksiyonu
 * // Custom row rendering
 * const customRowRender = (tr, dataItem) => {
 *   return React.cloneElement(tr, {
 *     ...tr.props,
 *     className: dataItem.status ? 'active-row' : 'inactive-row'
 *   });
 * };
 *
 
 */

const editField = "inEdit";

/**
 * @template T
 * @param {KendoDataGridProps<T={}>} props
 * @example
 * const MyGrid = () => {
 *   return (
 *     <KendoDataGrid
 *       columns={columns}
 *       columnUniqueKey="userGrid_v1"
 *       uri="/api/users"
 *       dataItemKey="id"
 *       dataState={initialState}
 *       gridButtons={gridButtons}
 *       title="User Management"
 *       pageable={true}
 *       selectable={true}
 *       onExcel={handleExcel}
 *       topButtons={customButtons}
 *       onSelectChange={handleSelectionChange}
 *       rowRender={customRowRender}
 *     />
 *   );
 * };
 * @returns
 */
export default function KendoDataGrid({
  hrefTarget = "_blank",
  notFetch = false,
  pageable = true,
  uri = "",
  selectable = false,
  childrenLocation = "Up",
  inLineEdit = false,
  onExcel = (t) => t,
  axios = axiosStatic,
  gridButtons = [],
  dataItemKey,
  dataState = { take: 10 },
  ...props
}) {
  const { columns, hrefProp, topButtons, fileName } = props;
  /**
   * @type {ReturnType<typeof React.useRef<Grid>>}
   */
  const gridRef = useRef(null);
  const { t } = useTranslation();
  const columnState = useState(columns);
  const idGetter = getter(hrefProp);

  const [filterInputValues, setFilterInputValues] = React.useState({});
  /**
   * @type {ReturnType<typeof React.useRef<ExcelExport>>}
   */
  const _export = React.useRef(null);
  const [selectedDataState, setSelectedDataState] = React.useState(dataState);
  const [excelStart, setExcelStart] = React.useState(false);
  const modifyState = React.useMemo(() => {
    return {
      ...selectedDataState,
      filter: {
        ...selectedDataState.filter,
        filters: [
          ...(selectedDataState.filter?.filters || []),
          ...(dataState.filter?.filters || []),
        ],
      },
    };
  }, [selectedDataState, dataState.filter]);
  const { data, isLoading, reFetch } = useDataFilterApi(axios, modifyState, {
    skip: !!notFetch,
    url: uri,
  });

  const dataStateChange = (event) => {
    setSelectedDataState(event.dataState);
  };
  const [selectedState, setSelectedState] = React.useState({});
  const excelExport = () => {
    if (_export.current !== null) {
      setExcelStart(true);
      var params = toDataSourceRequest({
        skip: 0,
        take: data.total,
        filter: modifyState.filter,
        sort: modifyState.sort,
      });
      const promise = axios.get(uri || "", {
        params: { ...params },
      });
      promise
        .then((result) => {
          result.data.data = onExcel(result.data.data);
          var editedData = excelDataNormalize(
            result.data.data,
            gridRef.current.columns,
            t
          );
          var sorted = gridRef.current.columns
            .filter((d) => !d.hidden)
            .sort((a, b) => {
              if (a.orderIndex > b.orderIndex) return 1;
              if (b.orderIndex > a.orderIndex) return -1;
              return 0;
            });
          sorted = sorted.map((d, i) => ({
            ...d,
            id: d.id.replace(/_(\d+)_column/, (match, key) => {
              // Key'i data objesinde bul ve yerine koy
              return `_${i}_column`;
            }),
          }));
          _export.current.save(editedData, sorted);
        })
        .finally(() => setExcelStart(false));
    }
  };

  const onHeaderSelectionChange = React.useCallback(
    (event) => {
      const checkboxElement = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState = {};
      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
      if (props.onSelectChange) {
        props.onSelectChange(newSelectedState, data.data);
      }
    },
    [idGetter, props, data.data]
  );

  const onSelectionChange = React.useCallback(
    (event) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: hrefProp,
      });
      setSelectedState(newSelectedState);
      if (props.onSelectChange) {
        props.onSelectChange(newSelectedState, data.data);
      }
    },
    [hrefProp, props, selectedState]
  );
  const [selectedList, setSelectedList] = React.useState({ data: [] });

  React.useEffect(() => {
    if (data) {
      setSelectedList({
        ...data,
        data: data?.data.map((item) => ({
          ...item,
          selected: selectedState[idGetter(item)],
        })),
      });
    }
  }, [data, idGetter, selectedState]);
  const selectedValue = React.useMemo(() => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : [];
    return list.findIndex((item) => !selectedState[idGetter(item)]) === -1;
  }, [data, idGetter, selectedState]);
  const guid = React.useMemo(() => uuid(), []);

  const expandChange = (event) => {
    let newData = data.data.map((item) => {
      if (item[props.dataItemKey] === event.dataItem[props.dataItemKey]) {
        item.expanded = !event.dataItem.expanded;
      }
      return item;
    });
    setSelectedList(newData);
  };

  const cleanFilters = () => {
    const newState = {
      ...selectedDataState,
      filter: { filters: [], logic: "and", skip: 0, take: 0 },
    };
    setFilterInputValues({});
    setSelectedDataState(newState);
  };

  return (
    <>
      <dataGridContext.Provider
        value={{
          data,
          reFetch,
          dataState: selectedDataState,
          exporter: _export.current,
          grid: gridRef.current,
          setSelectedList,
          columnState,
          initialColumns: columns,
          setFilterInputValues,
          filterInputValues,
        }}
      >
        {childrenLocation === "Up" && props.children}
        {isLoading && (
          <LoadingPanelComponent query={`#id${guid} .k-grid-content`} />
        )}
        {isLoading && <LinearProgress />}
        <span id={"id" + guid}>
          <ExcelExport ref={_export} />
          <Grid
            ref={gridRef}
            detail={props.detail}
            style={{ fontSize: 11 }}
            sortable={{ mode: "single", allowUnsort: true }}
            resizable
            expandField="expanded"
            onExpandChange={expandChange}
            reorderable={true}
            pageable={
              pageable
                ? {
                    buttonCount: 4,
                    pageSizes: props.take ? [props.take] : [10, 20, 50, 100],
                  }
                : false
            }
            rowRender={props.rowRender}
            data={selectedList}
            {...modifyState}
            filterable
            dataItemKey={hrefProp || props.dataItemKey}
            total={data?.total || 0}
            onDataStateChange={dataStateChange}
            editField={editField}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            selectedField={"selected"}
            onColumnReorder={(e) => {
              let count = 0;
              if (hrefProp) {
                count += 1;
              }
              if (selectable) {
                count += 1;
              }
              columnState[1](e.columns.slice(count));
            }}
            selectable={{
              enabled: selectable,
              drag: false,
              cell: false,
              mode: "multiple",
            }}
          >
            <GridToolbar>
              <CustomGridToolbar
                reFetch={reFetch}
                fileName={fileName}
                excelExport={excelExport}
                excelFetching={excelStart}
                dataState={selectedDataState}
                setDataState={setSelectedDataState}
                columnUniqueKey={props.columnUniqueKey}
                title={props.title}
                topButtons={topButtons}
                notFetch={notFetch}
                cleanFilters={cleanFilters}
              />
            </GridToolbar>
            {selectable && (
              <GridColumn
                field={"selected"}
                width="66px"
                headerSelectionValue={selectedValue}
                fixedColumn
                sortable={false}
              />
            )}
            {gridButtons.map((button) => (
              <GridColumn
                minResizableWidth={60}
                width="120px"
                filterable={false}
                sortable={false}
                cell={(props) => {
                  return (
                    <td>
                      <Button
                        sx={{ color: "white!important" }}
                        size="small"
                        component={Link}
                        to={
                          button.getUrl
                            ? button.getUrl(props.dataItem)
                            : props.dataItem[dataItemKey].toString()
                        }
                        variant="contained"
                        color="primary"
                        target={hrefTarget}
                      >
                        {button.title}
                      </Button>
                    </td>
                  );
                }}
              />
            ))}
            {columnState[0].map((column) => {
              const { field, columnMenu, filter, cell, title, width, ...rest } =
                column;

              return (
                <GridColumn
                  key={field}
                  field={field}
                  filter={filter}
                  filterCell={(e) =>
                    column.type === "boolean" ? (
                      <YesNoDropdownFilterCell {...e} />
                    ) : column.tKey ? (
                      <DropDownFilterCell {...e} filterTKey={rest.tKey} />
                    ) : column.filterCell ? (
                      <column.filterCell {...e} />
                    ) : (
                      <TextFilterCell {...e} />
                    )
                  }
                  columnMenu={column.columnMenu}
                  cell={(p) =>
                    column.hidden ? (
                      <td></td>
                    ) : (
                      <GridCellSelector cellProps={p} column={column} />
                    )
                  }
                  width={column.hidden || column.invisible ? 0 : width || 250}
                  title={t(title)}
                  {...rest}
                />
              );
            })}
          </Grid>
        </span>
        {childrenLocation === "Down" && props.children}
      </dataGridContext.Provider>
    </>
  );
}
